import objectPath from 'object-path'

const DROP_ATTR = 'data-designer-drop-id'
const ITEM_ATTR = 'data-designer-dnd-id'
const PARENT_ATTR = 'data-designer-dnd-parent'
const DRAGGING_ATTR = 'data-designer-dnd-dragging'
const PLACEHOLDER_ATTR = 'data-designer-dnd-placeholder'
const INDICATOR_ID = 'designer-custom-dnd-indicator'
const GHOST_ID = 'designer-custom-dnd-ghost'
const PLACEHOLDER_ID = 'designer-custom-dnd-placeholder'
const MIN_PLACEHOLDER_WIDTH = 32
const MIN_PLACEHOLDER_HEIGHT = 32
const DRAG_START_DISTANCE = 6
const FOCUS_DEBOUNCE_MS = 250
const PREVIEW_ANIMATION_MS = 160
let ACTIVE_DRAG_STATE = null
let LAST_FOCUS_STATE = {
  key: '',
  updatedAt: 0,
}

const STRUCTURAL_DROP_ALLOW = {
  panelGroup: ['panel'],
  tab: ['tabPanel'],
  dynamic: ['dynamicPanel', 'editPanel'],
  modal: ['modalBody', 'modalFooter'],
}

const INTERACTIVE_SELECTOR = [
  'a',
  'button',
  'input',
  'textarea',
  'select',
  '[aria-haspopup="listbox"]',
  '[role="combobox"]',
  '[role="listbox"]',
  '[role="option"]',
  '[contenteditable="true"]',
  '[role="button"]',
  '.MuiButtonBase-root',
  '[class*="-control"]',
  '[class*="-indicatorContainer"]',
  '[class*="-menu"]',
  '[class*="-option"]',
].join(',')

const FOCUS_SKIP_SELECTOR = [
  'a',
  'button',
  '[contenteditable="true"]',
  '[role="button"]',
  '.MuiButtonBase-root',
].join(',')

const getMapKey = (jsonForm, key) =>
  objectPath.get(jsonForm, `schema.mapKey.${key}`) || key || ''

const getMapTag = (jsonForm, key) =>
  objectPath.get(jsonForm, `schema.mapTag.${key}`) || key || ''

const getElementDepth = (element) => {
  let depth = 0
  let current = element

  while (current?.parentElement) {
    depth += 1
    current = current.parentElement
  }

  return depth
}

const getElementArea = (element) => {
  let rect = element.getBoundingClientRect()
  return rect.width * rect.height
}

const clamp = (value, min, max) =>
  Math.min(Math.max(value, min), Math.max(min, max))

const isVisibleElement = (element) => {
  if (!element || element.hidden) return false

  let rect = element.getBoundingClientRect()
  let style = window.getComputedStyle(element)

  return (
    rect.width > 0 &&
    rect.height > 0 &&
    style.display !== 'none' &&
    style.visibility !== 'hidden'
  )
}

const containsPoint = (element, x, y) => {
  let rect = element.getBoundingClientRect()

  return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom
}

const getDragDistance = (state, event) =>
  Math.abs(event.clientX - state.startX) +
  Math.abs(event.clientY - state.startY)

const hasRecentDesignerFocus = (key) =>
  LAST_FOCUS_STATE.key === key &&
  Date.now() - LAST_FOCUS_STATE.updatedAt < FOCUS_DEBOUNCE_MS

const markRecentDesignerFocus = (key) => {
  LAST_FOCUS_STATE = {
    key,
    updatedAt: Date.now(),
  }
}

const isInvalidDrop = (jsonForm, draggableId, dropId) => {
  if (!dropId || dropId === draggableId) return true

  let dragMapKey = getMapKey(jsonForm, draggableId)
  let dropMapKey = getMapKey(jsonForm, dropId)

  return (
    Boolean(dragMapKey) &&
    Boolean(dropMapKey) &&
    dropMapKey.indexOf(`${dragMapKey}.`) === 0
  )
}

const focusDesignerUi = ({ designContext, uischema, config }) => {
  if (!uischema?.isCanEdit) return false
  if (hasRecentDesignerFocus(uischema.key)) return true
  if (designContext.current?.ui?.key === uischema.key) {
    markRecentDesignerFocus(uischema.key)
    return true
  }
  if (typeof designContext.editUi !== 'function') return false

  markRecentDesignerFocus(uischema.key)
  designContext.editUi(uischema, config)

  if (typeof designContext.setNewUi === 'function') {
    designContext.setNewUi(null)
  }

  return true
}

const canDropDirectly = (jsonForm, draggableId, dropId) => {
  let dropTag = getMapTag(jsonForm, dropId)
  let allowList = STRUCTURAL_DROP_ALLOW[dropTag]

  if (!allowList) return true

  return allowList.includes(getMapTag(jsonForm, draggableId))
}

const getDistanceToElement = (element, x, y) => {
  let rect = element.getBoundingClientRect()
  let xDistance =
    x < rect.left ? rect.left - x : x > rect.right ? x - rect.right : 0
  let yDistance =
    y < rect.top ? rect.top - y : y > rect.bottom ? y - rect.bottom : 0

  return xDistance + yDistance
}

const getItemElement = (key, parentKey = '') =>
  Array.from(document.querySelectorAll(`[${ITEM_ATTR}]`)).find((element) => {
    if (element.getAttribute(ITEM_ATTR) !== key) return false
    if (parentKey && element.getAttribute(PARENT_ATTR) !== parentKey) {
      return false
    }

    return isVisibleElement(element)
  })

const getDirectChildDropElement = ({
  jsonForm,
  draggableId,
  dropId,
  x,
  y,
}) => {
  let childDropElements = Array.from(
    document.querySelectorAll(`[${PARENT_ATTR}]`),
  )
    .filter((element) => element.getAttribute(PARENT_ATTR) === dropId)
    .filter((element) => element.getAttribute(ITEM_ATTR) !== draggableId)
    .filter((element) => isVisibleElement(element))
    .map((element) => element.getAttribute(ITEM_ATTR))
    .map((key) =>
      Array.from(document.querySelectorAll(`[${DROP_ATTR}]`)).find(
        (element) => element.getAttribute(DROP_ATTR) === key,
      ),
    )
    .filter((element) => element && isVisibleElement(element))
    .filter(
      (element) =>
        !isInvalidDrop(jsonForm, draggableId, element.getAttribute(DROP_ATTR)),
    )
    .sort((a, b) => {
      let distanceDiff =
        getDistanceToElement(a, x, y) - getDistanceToElement(b, x, y)

      if (distanceDiff !== 0) return distanceDiff

      return getElementArea(a) - getElementArea(b)
    })

  return childDropElements[0] || null
}

const getNormalizedDropElement = ({
  jsonForm,
  draggableId,
  dropElement,
  x,
  y,
}) => {
  let dropId = dropElement.getAttribute(DROP_ATTR)

  if (canDropDirectly(jsonForm, draggableId, dropId)) return dropElement

  return getDirectChildDropElement({
    jsonForm,
    draggableId,
    dropId,
    x,
    y,
  })
}

const getDropElement = ({ jsonForm, draggableId, x, y }) => {
  let dropElements = Array.from(document.querySelectorAll(`[${DROP_ATTR}]`))
    .filter((element) => isVisibleElement(element))
    .filter((element) => containsPoint(element, x, y))
    .filter(
      (element) =>
        !isInvalidDrop(jsonForm, draggableId, element.getAttribute(DROP_ATTR)),
    )
    .sort((a, b) => {
      let areaDiff = getElementArea(a) - getElementArea(b)

      if (areaDiff !== 0) return areaDiff

      return getElementDepth(b) - getElementDepth(a)
    })

  if (!dropElements[0]) return null

  return getNormalizedDropElement({
    jsonForm,
    draggableId,
    dropElement: dropElements[0],
    x,
    y,
  })
}

const getSiblings = (dropId, draggableId) =>
  Array.from(document.querySelectorAll(`[${PARENT_ATTR}]`))
    .filter((element) => element.getAttribute(PARENT_ATTR) === dropId)
    .filter((element) => element.getAttribute(ITEM_ATTR) !== draggableId)
    .filter((element) => isVisibleElement(element))
    .map((element) => ({
      key: element.getAttribute(ITEM_ATTR),
      element,
      rect: element.getBoundingClientRect(),
    }))
    .sort((a, b) => {
      let topDiff = a.rect.top - b.rect.top

      if (Math.abs(topDiff) > 8) return topDiff

      return a.rect.left - b.rect.left
    })

const groupRows = (items) => {
  let rows = []

  items.forEach((item) => {
    let midY = item.rect.top + item.rect.height / 2
    let row = rows.find(
      ({ top, bottom }) => midY >= top - 8 && midY <= bottom + 8,
    )

    if (!row) {
      rows.push({
        top: item.rect.top,
        bottom: item.rect.bottom,
        items: [item],
      })
      return
    }

    row.top = Math.min(row.top, item.rect.top)
    row.bottom = Math.max(row.bottom, item.rect.bottom)
    row.items.push(item)
  })

  return rows
    .map((row) => ({
      ...row,
      items: row.items.sort((a, b) => a.rect.left - b.rect.left),
    }))
    .sort((a, b) => a.top - b.top)
}

const getPlaceholderRect = ({
  dropRect,
  left = dropRect.left + 4,
  top,
  width = dropRect.width - 8,
  height,
}) => {
  let maxWidth = Math.max(dropRect.width - 8, MIN_PLACEHOLDER_WIDTH)
  let maxHeight = Math.max(dropRect.height - 8, MIN_PLACEHOLDER_HEIGHT)
  let safeWidth = Math.max(
    MIN_PLACEHOLDER_WIDTH,
    Math.min(width, maxWidth),
  )
  let safeHeight = Math.max(
    MIN_PLACEHOLDER_HEIGHT,
    Math.min(height, maxHeight),
  )

  return {
    left: clamp(left, dropRect.left + 4, dropRect.right - safeWidth - 4),
    top: clamp(top, dropRect.top + 4, dropRect.bottom - safeHeight - 4),
    width: safeWidth,
    height: safeHeight,
  }
}

const getFullWidthPlaceholder = (dropRect, sourceRect, top) =>
  getPlaceholderRect({
    dropRect,
    top,
    height: sourceRect?.height || 48,
  })

const getRowPlaceholder = ({ dropRect, sourceRect, row, left }) =>
  getPlaceholderRect({
    dropRect,
    left,
    top: row.top,
    width: sourceRect?.width || row.items[0]?.rect.width || dropRect.width,
    height: row.bottom - row.top,
  })

const getOverlapSize = (startA, endA, startB, endB) =>
  Math.max(0, Math.min(endA, endB) - Math.max(startA, startB))

const getPreviewInsertData = ({
  dropRect,
  sourceRect,
  row,
  siblings,
  previewRect,
  dragDirectionX,
}) => {
  if (!previewRect) return null

  let rowOverlap = getOverlapSize(
    previewRect.top,
    previewRect.bottom,
    row.top,
    row.bottom,
  )

  if (rowOverlap <= 0) return null

  let dragForward = dragDirectionX >= 0
  let targetItem = row.items
    .map((item) => {
      let overlapX = getOverlapSize(
        previewRect.left,
        previewRect.right,
        item.rect.left,
        item.rect.right,
      )
      let overlapY = getOverlapSize(
        previewRect.top,
        previewRect.bottom,
        item.rect.top,
        item.rect.bottom,
      )
      let itemMid = item.rect.left + item.rect.width / 2
      let passMiddle = dragForward
        ? previewRect.right >= itemMid
        : previewRect.left <= itemMid

      return {
        ...item,
        item,
        overlapX,
        overlapY,
        passMiddle,
        overlapArea: overlapX * overlapY,
      }
    })
    .filter(({ overlapX, overlapY, passMiddle }) => {
      if (!passMiddle) return false
      if (overlapY <= 0) return false

      return overlapX >= MIN_PLACEHOLDER_WIDTH / 2
    })
    .sort((a, b) => {
      let areaDiff = b.overlapArea - a.overlapArea

      if (areaDiff !== 0) return areaDiff

      return dragForward ? b.rect.left - a.rect.left : a.rect.left - b.rect.left
    })[0]

  if (!targetItem) return null

  if (dragForward) {
    return {
      index: siblings.indexOf(targetItem.item) + 1,
      beforeKey: '',
      afterKey: targetItem.key,
      indicator: getRowPlaceholder({
        dropRect,
        sourceRect,
        row,
        left: targetItem.rect.right,
      }),
    }
  }

  return {
    index: siblings.indexOf(targetItem.item),
    beforeKey: targetItem.key,
    afterKey: '',
    indicator: getRowPlaceholder({
      dropRect,
      sourceRect,
      row,
      left: targetItem.rect.left,
    }),
  }
}

const getInsertData = ({
  dropElement,
  siblings,
  sourceRect,
  x,
  y,
  previewRect,
  dragDirectionX = 0,
}) => {
  let dropRect = dropElement.getBoundingClientRect()

  if (siblings.length === 0) {
    let height = sourceRect?.height || 48

    return {
      index: 0,
      beforeKey: '',
      afterKey: '',
      indicator: getFullWidthPlaceholder(dropRect, sourceRect, y - height / 2),
    }
  }

  let rows = groupRows(siblings)
  let firstRow = rows[0]
  let lastRow = rows[rows.length - 1]

  if (y < firstRow.top) {
    return {
      index: 0,
      beforeKey: firstRow.items[0]?.key || '',
      afterKey: '',
      indicator: getFullWidthPlaceholder(
        dropRect,
        sourceRect,
        firstRow.top - (sourceRect?.height || 48) / 2,
      ),
    }
  }

  if (y > lastRow.bottom) {
    let lastItem = siblings[siblings.length - 1]

    return {
      index: siblings.length,
      beforeKey: '',
      afterKey: lastItem?.key || '',
      indicator: getFullWidthPlaceholder(
        dropRect,
        sourceRect,
        lastRow.bottom - (sourceRect?.height || 48) / 2,
      ),
    }
  }

  let row =
    rows.find(({ top, bottom }) => y >= top - 8 && y <= bottom + 8) ||
    rows.reduce((closest, current) => {
      let currentMid = current.top + (current.bottom - current.top) / 2
      let closestMid = closest.top + (closest.bottom - closest.top) / 2

      return Math.abs(y - currentMid) < Math.abs(y - closestMid)
        ? current
        : closest
    }, firstRow)

  let previewInsertData = getPreviewInsertData({
    dropRect,
    sourceRect,
    row,
    siblings,
    previewRect,
    dragDirectionX,
  })

  if (previewInsertData) return previewInsertData

  let beforeItem = row.items.find(({ rect }) => x < rect.left + rect.width / 2)

  if (beforeItem) {
    return {
      index: siblings.indexOf(beforeItem),
      beforeKey: beforeItem.key,
      afterKey: '',
      indicator: getRowPlaceholder({
        dropRect,
        sourceRect,
        row,
        left: beforeItem.rect.left,
      }),
    }
  }

  let lastItem = row.items[row.items.length - 1]

  return {
    index: siblings.indexOf(lastItem) + 1,
    beforeKey: '',
    afterKey: lastItem.key,
    indicator: getRowPlaceholder({
      dropRect,
      sourceRect,
      row,
      left: lastItem.rect.right,
    }),
  }
}

const getDragPreviewPoint = (event, state) => {
  if (!state.ghostData) {
    return {
      x: event.clientX,
      y: event.clientY,
    }
  }

  let width = state.sourceRect?.width || 0
  let height = state.sourceRect?.height || 0
  let offsetX = state.ghostData.offsetX ?? width / 2
  let offsetY = state.ghostData.offsetY ?? height / 2

  return {
    x: event.clientX - offsetX + width / 2,
    y: event.clientY - offsetY + height / 2,
  }
}

const getDragPreviewRect = (event, state) => {
  if (!state.ghostData) return null

  let width = state.sourceRect?.width || 0
  let height = state.sourceRect?.height || 0
  let left = event.clientX - state.ghostData.offsetX
  let top = event.clientY - state.ghostData.offsetY

  return {
    left,
    top,
    right: left + width,
    bottom: top + height,
    width,
    height,
  }
}

const getDropData = (event, state) => {
  let pointerPoint = {
    x: event.clientX,
    y: event.clientY,
  }
  let previewPoint = getDragPreviewPoint(event, state)
  let previewRect = getDragPreviewRect(event, state)
  let sourceCenterX =
    state.sourceRect.left + (state.sourceRect?.width || 0) / 2
  let dragDirectionX = previewPoint.x >= sourceCenterX ? 1 : -1
  let dropElement =
    getDropElement({
      jsonForm: state.designContext.jsonForm,
      draggableId: state.draggableId,
      x: previewPoint.x,
      y: previewPoint.y,
    }) ||
    getDropElement({
      jsonForm: state.designContext.jsonForm,
      draggableId: state.draggableId,
      x: pointerPoint.x,
      y: pointerPoint.y,
    })

  if (!dropElement) return null

  let insertPoint = containsPoint(dropElement, previewPoint.x, previewPoint.y)
    ? previewPoint
    : pointerPoint

  dropElement = getNormalizedDropElement({
    jsonForm: state.designContext.jsonForm,
    draggableId: state.draggableId,
    dropElement,
    x: insertPoint.x,
    y: insertPoint.y,
  })

  if (!dropElement) return null

  let dropId = dropElement.getAttribute(DROP_ATTR)
  let siblings = getSiblings(dropId, state.draggableId)
  let insertData = getInsertData({
    dropElement,
    siblings,
    sourceRect: state.sourceRect,
    x: insertPoint.x,
    y: insertPoint.y,
    previewRect,
    dragDirectionX,
  })

  return {
    dropId,
    index: insertData.index,
    beforeKey: insertData.beforeKey,
    afterKey: insertData.afterKey,
    indicator: insertData.indicator,
    dropElement,
    siblings,
  }
}

const getIndicator = () => {
  let indicator = document.getElementById(INDICATOR_ID)

  if (indicator) return indicator

  indicator = document.createElement('div')
  indicator.id = INDICATOR_ID
  document.body.appendChild(indicator)

  return indicator
}

const renderIndicator = (dropData) => {
  let indicator = getIndicator()

  if (!dropData?.indicator) {
    indicator.style.display = 'none'
    return
  }

  let { left, top, width, height } = dropData.indicator

  Object.assign(indicator.style, {
    position: 'fixed',
    left: `${left}px`,
    top: `${top}px`,
    width: `${width}px`,
    height: `${height}px`,
    display: 'block',
    pointerEvents: 'none',
    zIndex: 2147483645,
    background: 'rgba(33, 150, 243, 0.18)',
    border: '1px dashed rgba(33, 150, 243, 0.85)',
    borderRadius: '4px',
    boxShadow: 'inset 0 0 0 1px rgba(33, 150, 243, 0.18)',
  })
}

const removeDragElements = () => {
  document.getElementById(INDICATOR_ID)?.remove()
  document.getElementById(GHOST_ID)?.remove()
  document.getElementById(PLACEHOLDER_ID)?.remove()
}

const createGhost = (sourceElement, event) => {
  let rect = sourceElement.getBoundingClientRect()
  let ghost = document.createElement('div')
  let clone = sourceElement.cloneNode(true)

  clone.removeAttribute(ITEM_ATTR)
  clone.removeAttribute(PARENT_ATTR)
  clone.removeAttribute(DROP_ATTR)
  clone.removeAttribute(DRAGGING_ATTR)
  clone
    .querySelectorAll?.(
      `[${ITEM_ATTR}], [${PARENT_ATTR}], [${DROP_ATTR}], [${DRAGGING_ATTR}]`,
    )
    .forEach((element) => {
      element.removeAttribute(ITEM_ATTR)
      element.removeAttribute(PARENT_ATTR)
      element.removeAttribute(DROP_ATTR)
      element.removeAttribute(DRAGGING_ATTR)
    })

  ghost.id = GHOST_ID
  Object.assign(ghost.style, {
    position: 'fixed',
    left: `${rect.left}px`,
    top: `${rect.top}px`,
    width: `${rect.width}px`,
    height: `${rect.height}px`,
    border: '1px solid rgba(33, 150, 243, 0.35)',
    background: '#fff',
    boxShadow: '0 10px 24px rgba(15, 23, 42, 0.18)',
    overflow: 'hidden',
    opacity: '0.96',
    pointerEvents: 'none',
    zIndex: 2147483646,
  })
  Object.assign(clone.style, {
    width: `${rect.width}px`,
    height: `${rect.height}px`,
    margin: '0',
    pointerEvents: 'none',
  })
  ghost.appendChild(clone)
  document.body.appendChild(ghost)

  return {
    ghost,
    offsetX: event.clientX - rect.left,
    offsetY: event.clientY - rect.top,
  }
}

const updateGhost = (state, event) => {
  if (!state.ghostData?.ghost) return

  state.ghostData.ghost.style.left = `${
    event.clientX - state.ghostData.offsetX
  }px`
  state.ghostData.ghost.style.top = `${
    event.clientY - state.ghostData.offsetY
  }px`
}

const createPlaceholder = (sourceElement, sourceRect) => {
  let placeholder = document.createElement(
    sourceElement.tagName?.toLowerCase?.() || 'div',
  )
  let body = document.createElement('div')
  let height = Math.max(sourceRect?.height || 0, MIN_PLACEHOLDER_HEIGHT)

  placeholder.id = PLACEHOLDER_ID
  placeholder.className = sourceElement.className || ''
  placeholder.setAttribute(PLACEHOLDER_ATTR, 'true')
  placeholder.setAttribute('aria-hidden', 'true')
  Object.assign(placeholder.style, {
    minHeight: `${height}px`,
    height: `${height}px`,
    pointerEvents: 'none',
    boxSizing: 'border-box',
  })
  Object.assign(body.style, {
    width: '100%',
    height: '100%',
    minHeight: `${height}px`,
    background: 'rgba(33, 150, 243, 0.12)',
    border: '1px dashed rgba(33, 150, 243, 0.85)',
    borderRadius: '4px',
    boxShadow: 'inset 0 0 0 1px rgba(33, 150, 243, 0.16)',
  })
  placeholder.appendChild(body)

  return placeholder
}

const getDropInsertParent = (dropElement) => {
  let rowElement = Array.from(dropElement?.children || []).find((element) =>
    element.classList?.contains('row'),
  )

  return rowElement || dropElement
}

const getPreviewReference = (state, dropData) => {
  if (!dropData) {
    return {
      parent: state.sourceElement?.parentElement,
      beforeElement: state.sourceElement,
    }
  }

  let beforeElement = dropData.beforeKey
    ? getItemElement(dropData.beforeKey, dropData.dropId)
    : null

  if (beforeElement) {
    return {
      parent: beforeElement.parentElement,
      beforeElement,
    }
  }

  let afterElement = dropData.afterKey
    ? getItemElement(dropData.afterKey, dropData.dropId)
    : null

  if (afterElement) {
    return {
      parent: afterElement.parentElement,
      beforeElement: afterElement.nextSibling,
    }
  }

  let siblingElement = dropData.siblings?.[0]?.element

  if (siblingElement) {
    return {
      parent: siblingElement.parentElement,
      beforeElement: siblingElement,
    }
  }

  return {
    parent: getDropInsertParent(dropData.dropElement),
    beforeElement: null,
  }
}

const getAnimationElements = (parents = [], sourceElement) =>
  Array.from(new Set(parents.filter(Boolean)))
    .flatMap((parent) => Array.from(parent.children || []))
    .filter((element) => element !== sourceElement)
    .filter((element) => element.id !== GHOST_ID)
    .filter((element) => element.id !== INDICATOR_ID)
    .filter((element) => isVisibleElement(element))

const captureElementRects = (elements) =>
  elements.reduce((rectMap, element) => {
    rectMap.set(element, element.getBoundingClientRect())
    return rectMap
  }, new Map())

const rememberPreviewStyle = (state, element) => {
  if (!state.previewStyleMap || state.previewStyleMap.has(element)) return

  state.previewStyleMap.set(element, {
    transform: element.style.transform,
    transition: element.style.transition,
    willChange: element.style.willChange,
  })
}

const animatePreviewLayout = (state, parents, updateLayout) => {
  let firstElements = getAnimationElements(parents, state.sourceElement)
  let firstRects = captureElementRects(firstElements)

  updateLayout()

  let lastElements = getAnimationElements(parents, state.sourceElement)
  let lastRects = captureElementRects(lastElements)

  Array.from(new Set([...firstElements, ...lastElements])).forEach(
    (element) => {
      let firstRect = firstRects.get(element)
      let lastRect = lastRects.get(element)

      if (!firstRect || !lastRect) return
      let x = firstRect.left - lastRect.left
      let y = firstRect.top - lastRect.top

      if (Math.abs(x) < 1 && Math.abs(y) < 1) return

      rememberPreviewStyle(state, element)
      element.style.transition = 'none'
      element.style.transform = `translate(${x}px, ${y}px)`
      element.style.willChange = 'transform'
      element.getBoundingClientRect()
      requestAnimationFrame(() => {
        if (ACTIVE_DRAG_STATE !== state) return

        element.style.transition = `transform ${PREVIEW_ANIMATION_MS}ms ease`
        element.style.transform = ''
      })
    },
  )
}

const restorePreviewStyles = (state) => {
  state.previewStyleMap?.forEach((style, element) => {
    element.style.transform = style.transform
    element.style.transition = style.transition
    element.style.willChange = style.willChange
  })
  state.previewStyleMap?.clear()
}

const renderSourcePlaceholder = (state) => {
  if (!state.placeholder || !state.sourceElement?.parentElement) return

  state.sourceElement.parentElement.insertBefore(
    state.placeholder,
    state.sourceElement,
  )
}

const renderDesignerPreview = (state, dropData) => {
  if (state.isPalette || !state.placeholder) return

  let reference = getPreviewReference(state, dropData)

  if (!reference.parent) {
    renderSourcePlaceholder(state)
    return
  }

  let parents = [
    state.placeholder.parentElement,
    state.sourceElement?.parentElement,
    reference.parent,
  ]

  animatePreviewLayout(state, parents, () => {
    reference.parent.insertBefore(state.placeholder, reference.beforeElement)
  })
}

const startDesignerDrag = (
  event,
  {
    designContext,
    uischema,
    draggableId = '',
    sourceDropId = '',
    sourceIndex = 0,
    isPalette = false,
    config = {},
  },
) => {
  let nativeEvent = event.nativeEvent || event
  let eventType = nativeEvent.type || ''
  let isMouseEvent = eventType.indexOf('mouse') === 0
  let isPointerEvent = eventType.indexOf('pointer') === 0

  if (ACTIVE_DRAG_STATE) return
  if (isPointerEvent && !nativeEvent.isPrimary) return
  if (nativeEvent.button !== undefined && nativeEvent.button !== 0) return
  let dragId = uischema?.key || draggableId

  if (!dragId) return
  if (!isPalette && typeof designContext.moveDesignerUi !== 'function') {
    return
  }
  if (isPalette && typeof designContext.createDesignerUi !== 'function') {
    return
  }
  let targetItem = !isPalette ? event.target?.closest?.(`[${ITEM_ATTR}]`) : null

  if (targetItem && targetItem.getAttribute(ITEM_ATTR) !== dragId) {
    return
  }

  let sourceElement =
    targetItem ||
    event.currentTarget.closest?.(`[${ITEM_ATTR}]`) ||
    event.currentTarget

  if (!sourceElement || !isVisibleElement(sourceElement)) return
  let isInteractiveTarget = event.target?.closest?.(INTERACTIVE_SELECTOR)

  let shouldFocusOnClick =
    !isPalette &&
    !event.target?.closest?.(FOCUS_SKIP_SELECTOR) &&
    uischema?.isCanEdit

  if (!isInteractiveTarget) {
    event.preventDefault()
    event.stopPropagation()
  }

  let oldOpacity = sourceElement.style.opacity
  let oldDisplay = sourceElement.style.display
  let oldPointerEvents = sourceElement.style.pointerEvents
  let oldUserSelect = document.body.style.userSelect
  let oldCursor = document.body.style.cursor
  let state = {
    designContext,
    uischema,
    config,
    draggableId: dragId,
    sourceDropId,
    sourceIndex,
    startX: nativeEvent.clientX,
    startY: nativeEvent.clientY,
    sourceRect: sourceElement.getBoundingClientRect(),
    isPalette,
    sourceElement,
    oldOpacity,
    oldDisplay,
    oldPointerEvents,
    oldUserSelect,
    oldCursor,
    hasDragged: false,
    hasFocused: false,
    shouldFocusOnClick,
    ghostData: null,
    placeholder: null,
    previewStyleMap: new Map(),
    dropData: null,
    moveEventNames: isMouseEvent
      ? ['mousemove']
      : ['pointermove', 'mousemove'],
    upEventNames: isMouseEvent ? ['mouseup'] : ['pointerup', 'mouseup'],
    cancelEventNames: isMouseEvent
      ? ['mouseleave']
      : ['pointercancel', 'mouseleave'],
  }
  ACTIVE_DRAG_STATE = state

  const beginDrag = () => {
    if (state.hasDragged) return

    state.hasDragged = true
    state.sourceRect = sourceElement.getBoundingClientRect()
    state.ghostData = createGhost(sourceElement, nativeEvent)
    if (state.isPalette) {
      sourceElement.style.opacity = '0.45'
    } else {
      state.placeholder = createPlaceholder(sourceElement, state.sourceRect)
      renderSourcePlaceholder(state)
      sourceElement.style.display = 'none'
      sourceElement.style.pointerEvents = 'none'
    }
    sourceElement.setAttribute(DRAGGING_ATTR, 'true')
    document.body.style.userSelect = 'none'
    document.body.style.cursor = 'grabbing'
  }

  const cleanup = () => {
    state.moveEventNames.forEach((eventName) => {
      document.removeEventListener(eventName, handleMove)
    })
    state.upEventNames.forEach((eventName) => {
      document.removeEventListener(eventName, handlePointerUp)
    })
    state.cancelEventNames.forEach((eventName) => {
      document.removeEventListener(eventName, handleCancel)
    })
    if (state.hasDragged) {
      restorePreviewStyles(state)
      sourceElement.style.opacity = state.oldOpacity
      sourceElement.style.display = state.oldDisplay
      sourceElement.style.pointerEvents = state.oldPointerEvents
      sourceElement.removeAttribute(DRAGGING_ATTR)
      document.body.style.userSelect = state.oldUserSelect
      document.body.style.cursor = state.oldCursor
      removeDragElements()
    }
    ACTIVE_DRAG_STATE = null
  }

  const handleMove = (moveEvent) => {
    if (!state.hasDragged) {
      if (getDragDistance(state, moveEvent) < DRAG_START_DISTANCE) return

      beginDrag()
    }

    moveEvent.preventDefault()
    moveEvent.stopPropagation()

    updateGhost(state, moveEvent)
    state.dropData = getDropData(moveEvent, state)
    renderDesignerPreview(state, state.dropData)
    renderIndicator(state.isPalette ? state.dropData : null)
  }

  const handlePointerUp = (upEvent) => {
    if (state.hasDragged) {
      let nextDropData = getDropData(upEvent, state)
      if (nextDropData) {
        state.dropData = nextDropData
      }
    }

    let dropData = state.dropData
    let hasDragged = state.hasDragged

    if (hasDragged) {
      upEvent.preventDefault()
      upEvent.stopPropagation()
    }

    cleanup()

    if (!hasDragged) {
      if (state.shouldFocusOnClick && !state.hasFocused) {
        state.hasFocused = true
        focusDesignerUi({ designContext, uischema, config })
      }
      return
    }

    if (!dropData) return

    if (state.isPalette) {
      designContext.createDesignerUi({
        draggableId: state.draggableId,
        source: {
          droppableId: state.sourceDropId,
          index: state.sourceIndex,
        },
        destination: {
          droppableId: dropData.dropId,
          index: dropData.index,
          beforeKey: dropData.beforeKey,
          afterKey: dropData.afterKey,
        },
      })
      return
    }

    designContext.moveDesignerUi({
      draggableId: state.draggableId,
      destinationKey: dropData.dropId,
      destinationIndex: dropData.index,
      destinationBeforeKey: dropData.beforeKey,
      destinationAfterKey: dropData.afterKey,
    })
  }

  const handleCancel = () => {
    cleanup()
  }

  state.moveEventNames.forEach((eventName) => {
    document.addEventListener(eventName, handleMove)
  })
  state.upEventNames.forEach((eventName) => {
    document.addEventListener(eventName, handlePointerUp)
  })
  state.cancelEventNames.forEach((eventName) => {
    document.addEventListener(eventName, handleCancel)
  })
}

export const getDesignerParentKey = (jsonForm, uischema = {}) => {
  let mapKey = getMapKey(jsonForm, uischema.key)
  let listKey = mapKey.split('.').filter(Boolean)

  return listKey.length > 1
    ? listKey[listKey.length - 2]
    : uischema.layout || ''
}

export const getDesignerDragItemProps = ({
  designContext,
  uischema,
  config = {},
}) => ({
  [ITEM_ATTR]: uischema?.key || '',
  [PARENT_ATTR]: getDesignerParentKey(designContext.jsonForm, uischema),
  'data-designer-dnd-index': uischema?.index ?? 0,
  onPointerDownCapture: (event) =>
    startDesignerDrag(event, { designContext, uischema, config }),
  onMouseDownCapture: (event) =>
    startDesignerDrag(event, { designContext, uischema, config }),
})

export const getDesignerFocusItemProps = ({
  designContext,
  uischema,
  config = {},
}) => {
  const handleFocus = (event) => {
    let nativeEvent = event.nativeEvent || event

    if (nativeEvent.button !== undefined && nativeEvent.button !== 0) return
    let targetItem = event.target?.closest?.(`[${ITEM_ATTR}]`)

    if (
      targetItem &&
      targetItem.getAttribute(ITEM_ATTR) !== (uischema?.key || '')
    ) {
      return
    }
    if (event.target?.closest?.(INTERACTIVE_SELECTOR)) return

    focusDesignerUi({ designContext, uischema, config })
  }

  return {
    onPointerDownCapture: handleFocus,
    onMouseDownCapture: handleFocus,
  }
}

export const getDesignerPaletteItemProps = ({
  designContext,
  draggableId,
  sourceDropId,
  sourceIndex,
}) => ({
  'data-designer-palette-id': draggableId || '',
  'data-designer-palette-source': sourceDropId || '',
  onPointerDownCapture: (event) =>
    startDesignerDrag(event, {
      designContext,
      draggableId,
      sourceDropId,
      sourceIndex,
      isPalette: true,
    }),
  onMouseDownCapture: (event) =>
    startDesignerDrag(event, {
      designContext,
      draggableId,
      sourceDropId,
      sourceIndex,
      isPalette: true,
    }),
})

export const getDesignerDropProps = (dropId) => ({
  [DROP_ATTR]: dropId || '',
})
